import { createTheme } from 'baseui';
import React from 'react';
import { MIcon } from '../../Components/Icon';
import { IIconSVG } from '../../Components/Icon/types';
import { ColorPalette } from '../base/colorPalette';
import * as components from './components/index';
import { ThemeColors } from './theme-colors';

export const CloseIcon = (props: IIconSVG) => {
	return <MIcon icon="close" size="small" color={props.color} />;
};

export const ArrowRightIcon = (props: IIconSVG) => {
	return <MIcon icon="keyboard_arrow_right" size="small" color={props.color} />;
};

export const ArrowLeftIcon = (props: IIconSVG) => {
	return <MIcon icon="keyboard_arrow_left" size="small" color={props.color} />;
};

const primitives = {
	...ColorPalette,
	primaryFontFamily: 'var(--epods--font-secondary-bold)',
};

const overrides = {
	...components.IconsOverwrite,
	breakpoints: {
		small: 1024,
		medium: 1600,
		large: 2560,
	},
	mediaQuery: {
		small: '@media screen and (min-width: 1024px)',
		medium: '@media screen and (min-width: 1600px)',
		large: '@media screen and (min-width: 2560px)',
	},
	colors: {
		...ThemeColors,
		// Components
		...components.Buttons,
		...components.Input,
		...components.Calendar,
		...components.Datepicker,
		...components.Tab,
		...components.Link,
		...components.Input,
		...components.Feedback,
		...components.Checkbox,
		...components.Toggle,
		...components.Panel,
		...components.Breadcrumbs,
		...components.Treeview,

		background: 'var(--epods--color-bg)',
		backgroundAlt: 'var(--epods--color-bg-canvas)',
		backgroundInv: 'var(--epods--color-bg-overlays)',
		foreground: 'var(--epods--color-text-primary)',
		foregroundAlt: 'var(--epods--color-tag-active)',
		foregroundInv: 'var(--epods--color-text-inverted)',

		accent: ColorPalette.focus,

		// Border
		borderStateDisabled: 'var(--epods-color-tag-outline-border-disabled)',
		borderAccent: 'var(--epods--color-signal-info-border)',
		borderAccentLight: 'var(--epods--color-signal-info-border)',
		borderNegative: 'var(--epods--color-signal-error-border)',
		borderWarning: 'var(--epods--color-signal-warning-border)',
		borderPositive: 'var(--epods--color-signal-success-border)',

		border: 'var(--epods--color-border)',
		borderAlt: '#AFAFAF',
		borderFocus: ColorPalette.focus,
		borderError: '#D44333',

		// Combobox
		comboboxListItemFocus: ColorPalette.focus,
		comboboxListItemHover: ThemeColors.mono300,

		shadowFocus: ColorPalette.focus,
		shadowError: 'rgba(229, 73, 55, 0.32)',

		// Slider/Toggle
		sliderTrackFill: 'var(--epods--color-toggle-active-uncheck)',
		sliderTrackFillHover: 'var(--epods--color-toggle-active-uncheck)',
		sliderTrackFillActive: '#AFAFAF',
		sliderTrackFillSelected: ThemeColors.primary,
		sliderTrackFillSelectedHover: ThemeColors.primary,
		sliderTrackFillSelectedActive: '#757575',
		sliderTrackFillDisabled: '#EEEEEE',
		sliderHandleFill: ThemeColors.white,
		sliderHandleFillHover: ThemeColors.white,
		sliderHandleFillActive: ThemeColors.white,
		sliderHandleFillSelected: ThemeColors.white,
		sliderHandleFillSelectedHover: ThemeColors.white,
		sliderHandleFillSelectedActive: ThemeColors.white,
		sliderHandleFillDisabled: '#CBCBCB',
		sliderHandleInnerFill: '#E2E2E2',
		sliderHandleInnerFillDisabled: 'var(--epods--color-toggle-inactive)',
		sliderHandleInnerFillSelectedHover: ThemeColors.primary,
		sliderHandleInnerFillSelectedActive: '#757575',

		sliderBorder: '#CBCBCB',
		sliderBorderHover: ThemeColors.black,
		sliderBorderDisabled: '#AFAFAF',

		// Menu
		menuFill: 'var(--epods--color-bg-third-elevation)',
		menuFillHover: 'var(--epods--color-bg-hover)',
		menuFontDefault: 'var(--epods--color-text-primary)',
		menuFontDisabled: '#CBCBCB',
		menuFontHighlighted: ThemeColors.black,
		menuFontSelected: ThemeColors.black,

		// Modal
		modalCloseColor: ThemeColors.mono1000,
		modalCloseColorHover: ThemeColors.mono800,
		modalCloseColorFocus: ColorPalette.focus,

		// Pagination
		paginationTriangleDown: '#545454',

		// Header navigation
		headerNavigationFill: 'transparent',

		// Spinner
		spinnerTrackFill: '#333333',
	},
	typography: {
		fontBaseP: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-p)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-p)',
		},
		fontBaseH1: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h1)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h1)',
		},
		fontBaseH2: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h2)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h2)',
		},
		fontBaseH3: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h3)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h3)',
		},
		fontBaseH4: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h4)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h4)',
		},
		fontBaseH5: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h5)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h5)',
		},
		fontBaseH6: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: 'var(--epods--font-size-h6)',
			fontWeight: 'normal',
			lineHeight: 'var(--epods--line-height-h6)',
		},
		font100: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '12px',
			fontWeight: 'normal',
			lineHeight: '20px',
		},
		font150: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '12px',
			fontWeight: '500',
			lineHeight: '20px',
		},
		font200: {
			fontFamily: 'var(--epods--font-secondary-semibold)', // tabs
			fontSize: 'var(--epods--font-size-p)',
			fontWeight: 'normal',
			lineHeight: '22px',
		},
		font250: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '14px',
			fontWeight: 500,
			lineHeight: '20px',
		},
		font300: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '16px',
			fontWeight: 'normal',
			lineHeight: '24px',
		},
		font350: {
			// modified from button settings
			fontFamily: 'var(--epods--font-secondary-semibold)',
			fontSize: '14px',
			fontWeight: 'normal',
			lineHeight: '18px',
		},
		font400: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '16px',
			fontWeight: 'normal',
			lineHeight: '28px',
		},
		font450: {
			// modified from button settings
			fontFamily: 'var(--epods--font-secondary-semibold)',
			fontSize: '16px',
			fontWeight: 'normal',
			lineHeight: '22px',
		},
		font550: {
			// modified from button settings
			fontFamily: 'var(--epods--font-secondary-semibold)',
			fontSize: '18px',
			fontWeight: 'normal',
			lineHeight: '28px',
		},
		font650: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '24px',
			fontWeight: 500,
			lineHeight: '32px',
		},
		font750: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '28px',
			fontWeight: 500,
			lineHeight: '36px',
		},
		font850: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '32px',
			fontWeight: 500,
			lineHeight: '40px',
		},
		font950: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '36px',
			fontWeight: 500,
			lineHeight: '44px',
		},
		font1050: {
			fontFamily: 'var(--epods--font-secondary)',
			fontSize: '40px',
			fontWeight: 500,
			lineHeight: '52px',
		},
		font1150: {
			fontFamily: 'var(--epods--font-secondary-bold)',
			fontSize: '36px',
			fontWeight: 500,
			lineHeight: '44px',
		},
		font1250: {
			fontFamily: 'var(--epods--font-secondary-bold)',
			fontSize: '44px',
			fontWeight: 500,
			lineHeight: '52px',
		},
		font1350: {
			fontFamily: 'var(--epods--font-secondary-bold)',
			fontSize: '52px',
			fontWeight: 500,
			lineHeight: '64px',
		},
		font1450: {
			fontFamily: 'var(--epods--font-secondary-bold)',
			fontSize: '96px',
			fontWeight: 500,
			lineHeight: '112px',
		},
	},
	sizing: {
		scale0: '2px',
		scale100: '4px',
		scale200: '6px',
		scale300: '8px',
		scale400: '10px',
		scale500: '12px',
		scale550: '8px', // Button
		scale600: '16px', // base
		scale650: '18px',
		scale700: '20px',
		scale750: '22px',
		scale800: '24px',
		scale900: '32px',
		scale1000: '40px',
		scale1200: '48px',
		scale1400: '56px',
		scale1600: '64px',
		scale2400: '96px',
		scale3200: '128px',
		scale4800: '192px',
	},
	lighting: {
		shadow400: '0 1px 4px 4px hsla(0, 0%, 0%, 0.16)',
		shadow500: '0 2px 8px 8px hsla(0, 0%, 0%, 0.16)',
		shadow600: '0 4px 16px hsla(0, 0%, 0%, 0.16)',
		shadow700: '0 8px 24px hsla(0, 0%, 0%, 0.16)',
		overlay0: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0)',
		overlay100: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.04)',
		overlay200: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.08)',
		overlay300: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.12)',
		overlay400: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.16)',
		overlay500: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.2)',
		overlay600: 'inset 0 0 0 1000px hsla(0, 0%, 0%, 0.24)',
	},
	borders: {
		borderCustom: '1px solid black',

		border100: {
			borderColor: 'hsla(0, 0%, 0%, 0.04)',
			borderStyle: 'solid',
			borderTopWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			borderRightWidth: '1px',
		},
		border200: {
			borderColor: 'hsla(0, 0%, 0%, 0.08)',
			borderStyle: 'solid',
			borderTopWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			borderRightWidth: '1px',
		},
		border300: {
			// zero is for table component
			borderColor: 'hsla(0, 0%, 0%, 0.12)',
			borderStyle: 'solid',
			borderWidth: '0px',
		},
		border400: {
			borderColor: 'hsla(0, 0%, 0%, 0.16)',
			borderStyle: 'solid',
			borderTopWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			borderRightWidth: '1px',
		},
		border500: {
			borderColor: 'hsla(0, 0%, 0%, 0.2)',
			borderStyle: 'solid',
			borderTopWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			borderRightWidth: '1px',
		},
		border600: {
			borderColor: 'hsla(0, 0%, 0%, 0.24)',
			borderStyle: 'solid',
			borderTopWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			borderRightWidth: '1px',
		},
		radius100: '2px',
		radius200: 'var(--epods--border-radius)',
		radius300: '8px',
		radius400: '12px',
		useRoundedCorners: true,
		buttonBorderRadius: 'var(--epods--border-radius)',
		inputBorderRadius: 'var(--epods--border-radius)',
		popoverBorderRadius: '0px',
		surfaceBorderRadius: '0px',
	},
	icons: {
		Delete: CloseIcon,
		ArrowRight: ArrowRightIcon,
		ArrowLeft: ArrowLeftIcon,
	},
	animation: {
		timing100: '0.25s',
		timing400: '0.4s',
		timing700: '0.6s',
		easeOutCurve: 'cubic-bezier(.2, .8, .4, 1)',
		easeInCurve: 'cubic-bezier(.8, .2, .6, 1)',
		easeInOutCurve: 'cubic-bezier(0.4, 0, 0.2, 1)',
	},
	direction: 'auto',
	zIndex: {
		modal: 2000,
	},
	name: 'epo-theme',
};

const baseTheme = createTheme(primitives, overrides);

//TODO: Review typing interface
export const Theme: any = {
	...baseTheme,
	//Add here custom colors and vars
	avatar: {
		...components.CustomAvatar,
	},
	textarea: {
		...components.CustomTextarea,
		...components.CustomTextareaColors,
	},
	input: {
		...components.CustomInput,
	},
	button: {
		...components.customButtons,
	},
	radio: { // TODO: check to delete
		...components.CustomRadio,
	},
	tab: {
		...components.CustomTab,
	},
	dropdown: {
		...components.CustomDropdown,
	},
	link: {
		...components.CustomLink,
	},
	feedback: {
		...components.CustomFeedback,
	},
	checkbox: { // TODO: check to delete
		...components.CustomCheckbox,
	},
	toggle: {
		...components.CustomToggle,
	},
	notification: {
		...components.CustomNotification,
	},
	tooltip: {
		...components.CustomTooltip,
	},
	panel: {
		...components.CustomPanel,
	},
	popover: {
		...components.CustomPopover,
	},
	dialog: {
		...components.CustomDialog,
	},
	slider: {
		...components.CustomSlider,
	},
	breadcrumbs: {
		...components.CustomBreadcrumbs,
	},
	calendar: {
		...components.CustomCalendar,
	},
	datepicker: {
		...components.CustomDatepicker,
	},
	card: {
		...components.CustomCard,
	},
	tag: {
		...components.CustomTag,
	},
	treeview: {
		...components.CustomTreeview,
	},
	list: {
		...components.List,
	},
	skeleton: {
		...components.Skeleton,
	},
	applicationNumber: {
		...components.ApplicationNumber,
	},
	spinner: {
		...components.Spinner,
	},
};
